import { z } from "zod";
import { ORG_ROLES, ROLES } from "./roles.js";
import { PERMISSIONS } from "./permissions.js";
/**
 * Zod response schemas for the M3 HTTP surface.
 *
 * The TypeScript interfaces in {@link "./types"} are the original
 * source of truth for response shapes; these schemas exist so the
 * OpenAPI generator (see `apps/api/src/openapi.config.ts`) can attach
 * a strongly-typed component to every route. Keep the two in sync —
 * the snapshot test in `apps/api/test/openapi-spec.snapshot.test.ts`
 * surfaces any drift.
 *
 * Naming convention: each exported schema ends in `…ResponseSchema`
 * so it's easy to find call sites and to namespace the DTO classes
 * (`SignupResponseDto`, `MeResponseDto`, …).
 */
const roleSchema = z.enum(ROLES);
const permissionSchema = z.enum(PERMISSIONS);
/**
 * Public-facing workspace shape (matches `PublicWorkspace`).
 */
export const publicWorkspaceSchema = z.object({
    id: z.string().uuid(),
    slug: z.string(),
    name: z.string(),
    logoUrl: z.string().url().nullable(),
    createdAt: z.string().datetime({ offset: true }),
});
/** Workspace + the caller's role in it. */
export const workspaceWithRoleSchema = z.object({
    id: z.string().uuid(),
    slug: z.string(),
    name: z.string(),
    role: roleSchema,
});
/** Public-facing user (matches `PublicUser`). */
export const publicUserSchema = z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    profilePictureUrl: z.string().url().nullable(),
});
// ----- /api/auth -----
/**
 * `POST /api/auth/signup` — magic link sent; user must check email.
 * Workspace was created server-side and is echoed back so the dashboard
 * can render the "we're sending the link to <addr>" copy.
 */
export const signupResponseSchema = z.object({
    pending: z.literal(true),
    email: z.string().email(),
    workspace: z.object({
        id: z.string().uuid(),
        slug: z.string(),
        name: z.string(),
    }),
});
/**
 * `POST /api/auth/signup` (ENG-35588 redesign) — user created, OTC
 * email dispatched, no session yet. The dashboard navigates to
 * `/verify-email?email=...` on this response.
 */
export const registerPendingResponseSchema = z.object({
    status: z.literal("pending_verification"),
    email: z.string().email(),
});
/**
 * `POST /api/auth/verify-email` (ENG-35588) — OTC accepted, email is
 * now verified at the provider. The dashboard redirects to `/login`
 * with a success toast.
 */
export const verifyEmailResponseSchema = z.object({
    verified: z.literal(true),
});
/**
 * `POST /api/auth/resend-verification` (ENG-35588) — uniform success
 * envelope. Returned the same way whether or not the email is known
 * (anti-enumeration).
 */
export const resendVerificationResponseSchema = z.object({
    status: z.literal("sent"),
});
/**
 * @deprecated Magic-link request response (M3). Schema kept for the
 *   `signupResponseSchema` magic-link path that no controller currently
 *   serves; TODO(magic-link).
 */
export const loginMagicAcceptedSchema = z.object({
    pending: z.literal(true),
});
/**
 * `POST /api/auth/login/magic/verify`, `POST /api/auth/login/password`,
 * `POST /api/auth/join`, `POST /api/workspaces`, `POST /api/workspaces/switch`
 * — session JWT delivered in the response body for the dashboard SPA to
 * store in localStorage and resend as `Authorization: Bearer <jwt>` on
 * every subsequent request.
 *
 * `token` is omitted on the "workspace-less" branch of password login
 * (`workspace === null`) ONLY if the caller cannot continue without a
 * workspace; in practice we always issue a token (workspace-less sessions
 * are valid — see ENG-35588) so the dashboard can POST to
 * `/api/workspaces`.
 */
export const sessionEstablishedSchema = z.object({
    token: z.string(),
    workspace: publicWorkspaceSchema.nullable(),
    role: roleSchema.nullable(),
});
/** `GET /api/auth/join/preview` — payload shown on the Join Workspace page. */
export const invitationPreviewSchema = z.object({
    workspace: publicWorkspaceSchema.pick({ id: true, slug: true, name: true }),
    // Built-in role key OR a custom `RoleDefinition.id` (B3).
    role: z.string().min(1).max(64),
    inviterEmail: z.string().email(),
    email: z.string().email(),
});
/** `POST /api/auth/join` — session minted for accepted invitee. */
export const joinResponseSchema = z.union([
    sessionEstablishedSchema,
    z.object({ pending: z.literal(true) }),
]);
/** Generic `{ ok: true }` acknowledgement used by destructive endpoints. */
export const okAcknowledgeSchema = z.object({ ok: z.literal(true) });
// ----- /api/me -----
/**
 * `GET /api/me` — 200 with the principal's profile. Unauthenticated
 * requests get 401 (not 200 + null); the dashboard's `AuthClient.me()`
 * translates a 401 to a `null` state so `<RequireAuth>` can branch:
 *   - 401 → AuthClient returns `null` → redirect to `/login`
 *   - `workspace: null` → redirect to `/no-workspace` (workspace-less, ENG-35588)
 *   - workspace present → render the wrapped route
 */
export const meResponseSchema = z.object({
    user: publicUserSchema,
    workspace: publicWorkspaceSchema.nullable(),
    /** @deprecated wire back-compat — derived from `roles`; read `roles` instead. */
    role: roleSchema.nullable(),
    /** Namespaced role tokens (`ws:admin`, `org:org_admin`, `platform:staff`, …). */
    roles: z.array(z.string()),
    permissions: z.array(permissionSchema),
    canCreateWorkspace: z.boolean(),
    /** Resolved feature flags (B2) — defaults folded server-side, so every
     *  registered key is present even for tokens minted before a flag existed. */
    flags: z.record(z.union([z.boolean(), z.number(), z.string()])),
});
// ----- /api/workspaces -----
export const workspaceListResponseSchema = z.array(workspaceWithRoleSchema);
// ----- /api/workspaces/:slug/members -----
export const publicMemberSchema = z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    profilePictureUrl: z.string().url().nullable(),
    // A built-in workspace role key OR a custom `RoleDefinition.id` (B3); the
    // dashboard resolves custom ids to names via the roles list.
    role: z.string().min(1).max(64),
    joinedAt: z.string().datetime({ offset: true }),
});
export const memberListResponseSchema = z.array(publicMemberSchema);
// ----- /api/org/members -----
const orgRoleSchema = z.enum(ORG_ROLES);
/** Org-membership entry (matches `OrgMember`). One tier up from a member. */
export const orgMemberSchema = z.object({
    id: z.string().uuid(),
    userId: z.string().uuid(),
    email: z.string().email(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    profilePictureUrl: z.string().url().nullable(),
    role: orgRoleSchema,
    status: z.string(),
    joinedAt: z.string().datetime({ offset: true }),
    isSelf: z.boolean(),
});
export const orgMemberListResponseSchema = z.array(orgMemberSchema);
// ----- /api/org/workspaces -----
/** Org-scoped workspace summary (matches `OrgWorkspace`). */
export const orgWorkspaceSchema = z.object({
    id: z.string().uuid(),
    slug: z.string(),
    name: z.string(),
    environment: z.string(),
    onboardingState: z.string(),
    createdAt: z.string().datetime({ offset: true }),
    memberCount: z.number().int(),
});
export const orgWorkspaceListResponseSchema = z.array(orgWorkspaceSchema);
// ----- /api/workspaces/:slug/invitations -----
export const publicInvitationSchema = z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    // Built-in role key OR a custom `RoleDefinition.id` (B3).
    role: z.string().min(1).max(64),
    invitedBy: z.object({
        id: z.string().uuid(),
        email: z.string().email(),
    }),
    expiresAt: z.string().datetime({ offset: true }),
    createdAt: z.string().datetime({ offset: true }),
});
export const invitationListResponseSchema = z.array(publicInvitationSchema);
// ----- /api/workspaces/:slug/api-keys -----
/**
 * Listing shape — never includes the raw token (only the prefix).
 */
export const publicApiKeySchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    prefix: z.string(),
    expiresAt: z.string().datetime({ offset: true }).nullable(),
    lastUsedAt: z.string().datetime({ offset: true }).nullable(),
    createdAt: z.string().datetime({ offset: true }),
    createdBy: z.object({
        id: z.string().uuid(),
        email: z.string().email(),
    }),
    revokedAt: z.string().datetime({ offset: true }).nullable(),
});
export const apiKeyListResponseSchema = z.array(publicApiKeySchema);
/**
 * `POST /api/workspaces/:slug/api-keys` — the ONLY response in the API
 * surface that contains the raw `token`. See FD § api key endpoints.
 */
export const apiKeyWithTokenSchema = publicApiKeySchema.extend({
    token: z.string(),
});
// ----- /health -----
export const healthLiveSchema = z.object({
    status: z.literal("ok"),
    uptime: z.number(),
    timestamp: z.string().datetime({ offset: true }),
});
/**
 * `@nestjs/terminus` shape — kept loose because Terminus owns the
 * detail keys (one per indicator). Documented here for completeness.
 */
export const healthReadyResultSchema = z.object({
    status: z.union([z.literal("ok"), z.literal("error"), z.literal("shutting_down")]),
    info: z.record(z.record(z.unknown())).optional(),
    error: z.record(z.record(z.unknown())).optional(),
    details: z.record(z.record(z.unknown())),
});
//# sourceMappingURL=api-responses.js.map